import {
  MASTER_LIST_FILTER_ANY,
  formatMasterListQuestionCountLabel,
  getMasterListColumns,
  type MasterListColumnKey,
  type MasterListRow,
} from './masterListUtils';

export const CAPABILITY_CATALOG_PAGE_SIZE = 20;

/** Source type + framework: toolbar filters only — hidden from catalog table columns. */
export const CATALOG_INLINE_FILTER_EXCLUDED_KEYS: MasterListColumnKey[] = [
  'frameworkType',
  'sourceFrameworkName',
];

/** Count columns — display only (no inline filters or API column filters). */
export const CATALOG_METRIC_COLUMN_KEYS: MasterListColumnKey[] = [
  'subCapabilityCount',
  'questionCount',
];

const CATALOG_NON_INLINE_FILTER_COLUMN_KEYS: MasterListColumnKey[] = [
  'capabilityName',
  ...CATALOG_METRIC_COLUMN_KEYS,
];

const CATALOG_NON_QUERY_COLUMN_FILTER_KEYS: MasterListColumnKey[] = [
  ...CATALOG_INLINE_FILTER_EXCLUDED_KEYS,
  ...CATALOG_NON_INLINE_FILTER_COLUMN_KEYS,
];

const CATALOG_COLUMN_ORDER: MasterListColumnKey[] = [
  'capabilityName',
  'capabilityIsActive',
  'frameworkReferenceCompany',
  'businessTypeName',
  'subCapabilityCount',
  'questionCount',
];

const CATALOG_COLUMN_WIDTH_OVERRIDES: Partial<Record<MasterListColumnKey, string>> = {
  capabilityName: '35.5%',
  capabilityIsActive: '9.5%',
  frameworkReferenceCompany: '9.5%',
  businessTypeName: '8.5%',
  subCapabilityCount: '7.5%',
  questionCount: '7.5%',
};

export const isCapabilityCatalogMetricColumn = (key: MasterListColumnKey) =>
  CATALOG_METRIC_COLUMN_KEYS.includes(key);

export const getCapabilityCatalogColumns = () => {
  const byKey = new Map(
    getMasterListColumns('capability')
      .filter((column) => !CATALOG_INLINE_FILTER_EXCLUDED_KEYS.includes(column.key))
      .map((column) => [column.key, column])
  );

  return CATALOG_COLUMN_ORDER.map((key) => byKey.get(key)).flatMap((column) => {
    if (!column) return [];
    const metric = isCapabilityCatalogMetricColumn(column.key);
    return [
      {
        ...column,
        width: CATALOG_COLUMN_WIDTH_OVERRIDES[column.key] ?? column.width,
        filterable:
          metric || column.key === 'capabilityName' ? false : column.filterable,
      },
    ];
  });
};

export const getCapabilityCatalogFilterableColumns = () =>
  getCapabilityCatalogColumns().filter((column) => column.filterable !== false);

export type CapabilityCatalogQuery = {
  pageNumber: number;
  pageSize: number;
  sort?: string;
  search?: string;
  frameworkId?: number;
  sourceTypeId?: number;
  capabilityName?: string;
  capabilityIsActive?: string;
  frameworkType?: string;
  sourceFrameworkName?: string;
  frameworkReferenceCompany?: string;
  businessTypeName?: string;
  subCapabilityCount?: string;
  questionCount?: string;
};

export type CapabilityCatalogSubItem = {
  subId: number;
  capabilityId: number;
  subCapabilityName: string;
  isActive?: boolean;
  questionCount: number;
};

export type CapabilityCatalogItem = {
  id: number;
  businessTypeId?: number | null;
  businessTypeName?: string;
  frameworkId?: number | null;
  sourceFrameworkName?: string;
  frameworkReferenceCompany?: string;
  frameworkType?: string;
  capabilityName?: string;
  isActive?: boolean;
  subCapabilityCount?: number;
  questionCount?: number;
  subCapabilities?: CapabilityCatalogSubItem[];
};

export type CapabilityCatalogFilterOptions = Partial<Record<MasterListColumnKey, string[]>>;

const COLUMN_QUERY_KEY: Partial<Record<MasterListColumnKey, keyof CapabilityCatalogQuery>> = {
  capabilityName: 'capabilityName',
  capabilityIsActive: 'capabilityIsActive',
  frameworkType: 'frameworkType',
  sourceFrameworkName: 'sourceFrameworkName',
  frameworkReferenceCompany: 'frameworkReferenceCompany',
  businessTypeName: 'businessTypeName',
  subCapabilityCount: 'subCapabilityCount',
  questionCount: 'questionCount',
};

const pickColumnFilterValue = (value: string | undefined) => {
  const selected = value || MASTER_LIST_FILTER_ANY;
  if (selected === MASTER_LIST_FILTER_ANY) return undefined;
  return selected;
};

export const buildCapabilityCatalogQuery = (input: {
  pageNumber: number;
  pageSize?: number;
  search?: string;
  frameworkId?: string;
  sourceTypeId?: string;
  columnFilters: Record<MasterListColumnKey, string>;
}): CapabilityCatalogQuery => {
  const query: CapabilityCatalogQuery = {
    pageNumber: input.pageNumber,
    pageSize: input.pageSize ?? CAPABILITY_CATALOG_PAGE_SIZE,
    sort: 'framework-asc',
    search: input.search?.trim() || undefined,
  };

  const frameworkId = Number(input.frameworkId);
  if (Number.isFinite(frameworkId) && frameworkId > 0) {
    query.frameworkId = frameworkId;
  }

  const sourceTypeId = Number(input.sourceTypeId);
  if (Number.isFinite(sourceTypeId) && sourceTypeId > 0) {
    query.sourceTypeId = sourceTypeId;
  }

  for (const [columnKey, paramKey] of Object.entries(COLUMN_QUERY_KEY) as Array<
    [MasterListColumnKey, keyof CapabilityCatalogQuery]
  >) {
    if (CATALOG_NON_QUERY_COLUMN_FILTER_KEYS.includes(columnKey)) continue;
    const value = pickColumnFilterValue(input.columnFilters[columnKey]);
    if (value != null) {
      (query as Record<string, string | number | undefined>)[paramKey] = value;
    }
  }

  return query;
};

const normalizeCatalogItem = (raw: Record<string, unknown>): CapabilityCatalogItem => ({
  id: Number(raw.id ?? raw.Id ?? 0),
  businessTypeId: (raw.businessTypeId ?? raw.BusinessTypeId) as number | null | undefined,
  businessTypeName: String(raw.businessTypeName ?? raw.BusinessTypeName ?? ''),
  frameworkId: (raw.frameworkId ?? raw.FrameworkId) as number | null | undefined,
  sourceFrameworkName: String(raw.sourceFrameworkName ?? raw.SourceFrameworkName ?? ''),
  frameworkReferenceCompany: String(
    raw.frameworkReferenceCompany ?? raw.FrameworkReferenceCompany ?? ''
  ),
  frameworkType: String(raw.frameworkType ?? raw.FrameworkType ?? ''),
  capabilityName: String(raw.capabilityName ?? raw.CapabilityName ?? ''),
  isActive: (raw.isActive ?? raw.IsActive) as boolean | undefined,
  subCapabilityCount: Number(raw.subCapabilityCount ?? raw.SubCapabilityCount ?? 0),
  questionCount: Number(raw.questionCount ?? raw.QuestionCount ?? 0),
  subCapabilities: (Array.isArray(raw.subCapabilities ?? raw.SubCapabilities)
    ? (raw.subCapabilities ?? raw.SubCapabilities)
    : []
  ).map((sub) => {
    const row = sub as Record<string, unknown>;
    return {
      subId: Number(row.subId ?? row.SubId ?? 0),
      capabilityId: Number(row.capabilityId ?? row.CapabilityId ?? 0),
      subCapabilityName: String(row.subCapabilityName ?? row.SubCapabilityName ?? ''),
      isActive: (row.isActive ?? row.IsActive) as boolean | undefined,
      questionCount: Number(row.questionCount ?? row.QuestionCount ?? 0),
    };
  }),
});

export const normalizeCapabilityCatalogPage = (data: Record<string, unknown> | null | undefined) => {
  const itemsRaw = Array.isArray(data?.items)
    ? data.items
    : Array.isArray(data?.Items)
      ? data.Items
      : [];

  return {
    items: itemsRaw.map((item) => normalizeCatalogItem(item as Record<string, unknown>)),
    totalCount: Number(data?.totalCount ?? data?.TotalCount ?? 0),
    pageNumber: Number(data?.pageNumber ?? data?.PageNumber ?? 1),
    pageSize: Number(data?.pageSize ?? data?.PageSize ?? CAPABILITY_CATALOG_PAGE_SIZE),
  };
};

export const normalizeCapabilityCatalogFilterOptions = (
  data: Record<string, unknown> | null | undefined
): CapabilityCatalogFilterOptions => {
  const byColumn =
    (data?.optionsByColumn as Record<string, string[]>) ??
    (data?.OptionsByColumn as Record<string, string[]>) ??
    {};

  const next: CapabilityCatalogFilterOptions = {};
  for (const [key, values] of Object.entries(byColumn)) {
    next[key as MasterListColumnKey] = Array.isArray(values) ? values : [];
  }
  return next;
};

export const mergeCatalogFilterOptionExtras = (
  options: CapabilityCatalogFilterOptions,
  extras?: Partial<Record<MasterListColumnKey, string[]>>
): CapabilityCatalogFilterOptions => {
  if (!extras) return options;
  const next = { ...options };
  for (const [key, extraValues] of Object.entries(extras) as Array<
    [MasterListColumnKey, string[] | undefined]
  >) {
    const base = new Set(next[key] || []);
    for (const value of extraValues || []) {
      const trimmed = String(value || '').trim();
      if (trimmed) base.add(trimmed);
    }
    next[key] = [...base].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }
  return next;
};

export const sanitizeCatalogColumnFilters = (
  filters: Record<MasterListColumnKey, string>,
  options: CapabilityCatalogFilterOptions,
  filterableKeys: MasterListColumnKey[]
): Record<MasterListColumnKey, string> => {
  const next = { ...filters };
  let changed = false;
  for (const key of CATALOG_NON_QUERY_COLUMN_FILTER_KEYS) {
    if ((next[key] || MASTER_LIST_FILTER_ANY) !== MASTER_LIST_FILTER_ANY) {
      next[key] = MASTER_LIST_FILTER_ANY;
      changed = true;
    }
  }
  for (const key of filterableKeys) {
    const selected = next[key] || MASTER_LIST_FILTER_ANY;
    if (selected === MASTER_LIST_FILTER_ANY) continue;
    const allowed = options[key] || [];
    if (!allowed.includes(selected)) {
      next[key] = MASTER_LIST_FILTER_ANY;
      changed = true;
    }
  }
  return changed ? next : filters;
};

export const mapCapabilityCatalogItemsToMasterList = (
  items: CapabilityCatalogItem[]
): {
  rows: MasterListRow[];
  subsByCapabilityId: Map<number, MasterListRow[]>;
} => {
  const rows: MasterListRow[] = [];
  const subsByCapabilityId = new Map<number, MasterListRow[]>();

  for (const item of items) {
    const capabilityId = item.id;
    if (!capabilityId) continue;

    const context = {
      capabilityId,
      capabilityName: item.capabilityName || '',
      capabilityDescription: '',
      capabilityIsActive: item.isActive !== false,
      frameworkId: item.frameworkId ?? null,
      sourceFrameworkName: item.sourceFrameworkName || '',
      frameworkReferenceCompany: item.frameworkReferenceCompany || '',
      frameworkType: item.frameworkType || '',
      frameworkDescription: '',
      businessTypeId: item.businessTypeId ?? null,
      businessTypeName: item.businessTypeName || '',
    };

    rows.push({
      rowKey: `cap-${capabilityId}`,
      viewMode: 'capability',
      ...context,
      subId: 0,
      subCapabilityName: '',
      subCapabilityDescription: '',
      subIsActive: true,
      subCapabilityCount: item.subCapabilityCount ?? 0,
      subCapabilitySummary: (item.subCapabilities || [])
        .map((sub) => sub.subCapabilityName)
        .filter(Boolean)
        .join(', '),
      questionCount: item.questionCount ?? 0,
      questionId: 0,
      questionText: '',
      questionState: '',
      questionDisplayOrder: 0,
      questionIsActive: true,
      currentStateQuestions: [],
      futureStateQuestions: [],
    });

    const subRows: MasterListRow[] = (item.subCapabilities || []).map((sub) => ({
      rowKey: `sub-${sub.subId}`,
      viewMode: 'subCapability' as const,
      ...context,
      subId: sub.subId,
      subCapabilityName: sub.subCapabilityName || '',
      subCapabilityDescription: '',
      subIsActive: sub.isActive !== false,
      subCapabilityCount: 0,
      subCapabilitySummary: '',
      questionCount: sub.questionCount ?? 0,
      questionId: 0,
      questionText: '',
      questionState: '',
      questionDisplayOrder: 0,
      questionIsActive: true,
      currentStateQuestions: [],
      futureStateQuestions: [],
    }));

    subsByCapabilityId.set(capabilityId, subRows);
  }

  return { rows, subsByCapabilityId };
};

export const formatSubQuestionCountLabel = (count: number) =>
  formatMasterListQuestionCountLabel(count);

const UNASSIGNED_FRAMEWORK_GROUP_KEY = '__unassigned__';

export type CapabilityCatalogFrameworkGroup = {
  key: string;
  title: string;
  subtitle: string;
  isUnassigned: boolean;
  rows: MasterListRow[];
};

export function getCatalogFrameworkGroupKey(row: MasterListRow): string {
  const name = String(row.sourceFrameworkName || '').trim();
  if (!name) return UNASSIGNED_FRAMEWORK_GROUP_KEY;
  if (row.frameworkId != null) return `fw:${row.frameworkId}`;
  return `fw-name:${name.toLowerCase()}`;
}

/** Groups a catalog page of rows by source framework (stable order; unassigned last). */
export function groupCatalogRowsByFramework(
  rows: MasterListRow[]
): CapabilityCatalogFrameworkGroup[] {
  if (!rows.length) return [];

  const buckets = new Map<string, MasterListRow[]>();
  const order: string[] = [];

  for (const row of rows) {
    const key = getCatalogFrameworkGroupKey(row);
    if (!buckets.has(key)) {
      buckets.set(key, []);
      order.push(key);
    }
    buckets.get(key)!.push(row);
  }

  return order.map((key) => {
    const groupRows = buckets.get(key) || [];
    const sample = groupRows[0];
    const isUnassigned = key === UNASSIGNED_FRAMEWORK_GROUP_KEY;
    const title = isUnassigned
      ? 'No source framework'
      : String(sample?.sourceFrameworkName || '').trim() || 'Source framework';
    const refCompany = String(sample?.frameworkReferenceCompany || '').trim();
    const subtitle = isUnassigned ? '' : refCompany;

    return {
      key,
      title,
      subtitle,
      isUnassigned,
      rows: groupRows,
    };
  });
}

/** Reserved for columns hidden under framework groups (none — ref. company shown per row). */
export const CATALOG_FRAMEWORK_DUPLICATE_COLUMN_KEYS: MasterListColumnKey[] = [];
